/**
 * `/sign-up` view — issue #117.
 *
 * Server contract: POST /auth/sign-up returns 202 + sends an email
 * confirmation link. The view flips to a "check your inbox" success
 * panel on 202.
 *
 * Client-side validation:
 *   - password length ≥ 12 (server-side regex also enforces, but a
 *     local check avoids a round-trip + spotlights the issue inline)
 *
 * Recovery email is NOT collected here — it's set later in account settings
 * (`account-view.ts`) to keep sign-up frictionless.
 *
 * The Terms / Privacy checkbox is scaffolded (per CLAUDE.md SaaS norm)
 * but its target route does not exist yet — open scope question called
 * out in the PR body for follow-up.
 */
import { button, card, textInput } from '@dl/ui';
import {
  div,
  form,
  h1,
  input,
  label,
  main,
  p,
  span,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';
import { appRouteLink, type AppRoute } from '../router.js';

import { signUp } from './api.js';
import {
  clearFormErrors,
  focusFirstError,
  renderAuthSuccess,
  setFieldError,
  setFormError,
  setSubmitting,
} from './forms.js';

const MIN_PASSWORD_LENGTH = 12;

interface SignUpLocalState {
  successEmail: string | null;
}

const localState: SignUpLocalState = { successEmail: null };

async function onSubmit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const formEl = event.currentTarget as HTMLFormElement;
  clearFormErrors(formEl);
  const data = new FormData(formEl);
  const email = String(data.get('email') ?? '').trim();
  const password = String(data.get('password') ?? '');

  let hasError = false;
  if (email.length === 0) {
    setFieldError(formEl, 'sign-up-email', t('auth.sign_up.email_label'));
    hasError = true;
  }
  if (password.length < MIN_PASSWORD_LENGTH) {
    setFieldError(formEl, 'sign-up-password', t('auth.sign_up.error_short'));
    hasError = true;
  }
  if (hasError) {
    focusFirstError(formEl);
    return;
  }

  setSubmitting(formEl, true);
  // Recovery email is no longer collected at sign-up — it's set later in
  // account settings (less sign-up friction; recovery-email separation is a
  // deliberate post-signup security choice).
  const result = await signUp({ email, password });
  setSubmitting(formEl, false);

  if (result.ok) {
    localState.successEmail = email;
    // Re-render via location.hash bump on the same route — the simplest
    // way to flip the view to its success panel without dragging the
    // whole sign-up state into the Model.
    const event = new CustomEvent('dl-auth-rerender');
    window.dispatchEvent(event);
    // Fallback: replace the form DOM directly with the success panel.
    const card = formEl.closest<HTMLElement>('.auth-card');
    if (card) {
      renderSuccess(card, email);
    }
    return;
  }
  if (result.status === 409) {
    setFieldError(formEl, 'sign-up-email', t('auth.sign_up.error_exists'));
    focusFirstError(formEl);
    return;
  }
  if (result.status === 403 && result.code === 'signup_not_allowed') {
    // Invite-only gate (admin signup allowlist). Generic message — the
    // server gives no signal about whether the email is listed.
    setFormError(formEl, t('auth.sign_up.error_invite_only'));
    return;
  }
  setFormError(formEl, result.error);
}

const handleSubmit = (event: Event): void => {
  void onSubmit(event as SubmitEvent);
};

function renderSuccess(card: HTMLElement, email: string): void {
  renderAuthSuccess(card, {
    heading: t('auth.sign_up.success_heading'),
    body: t('auth.sign_up.success_body', { email }),
    bodyTestid: 'sign-up-success',
    linkHref: '/sign-in',
    linkText: t('auth.sign_in.heading'),
    linkTestid: 'sign-up-back-to-sign-in',
  });
}

export function viewSignUp<Model, Msg>(_state: Signal<Model>, send: Send<Msg>): Mountable {
  // If we just succeeded (state held across re-renders in the same
  // mount), render the success panel directly.
  if (localState.successEmail !== null) {
    const email = localState.successEmail;
    return main({ role: 'main', class: 'auth-shell', 'data-testid': 'sign-up-view' }, [
      card({ variant: 'default', padding: 'xl', className: 'auth-card auth-card--result', children: [
        div({ class: 'auth-card-head' }, [
          span({ class: 'auth-result-glyph auth-result-glyph--success', 'aria-hidden': 'true' }, [
            text('✓'),
          ]),
          h1([text(t('auth.sign_up.success_heading'))]),
        ]),
        p({ class: 'auth-card-subtitle', 'data-testid': 'sign-up-success' }, [
          text(t('auth.sign_up.success_body', { email })),
        ]),
        div({ class: 'auth-form-links auth-form-links--center' }, [
          appRouteLink(
            send,
            { kind: 'sign-in' } as AppRoute,
            { 'data-testid': 'sign-up-back-to-sign-in' },
            [text(t('auth.sign_in.heading'))],
            (route) => ({ type: 'navigate', route }) as unknown as Msg,
          ),
        ]),
      ] }),
    ]);
  }

  return main({ role: 'main', class: 'auth-shell', 'data-testid': 'sign-up-view' }, [
    card({ variant: 'default', padding: 'xl', className: 'auth-card', children: [
      div({ class: 'auth-card-head' }, [
        h1([text(t('auth.sign_up.heading'))]),
        p({ class: 'auth-card-subtitle' }, [text(t('auth.sign_up.subtitle'))]),
      ]),
      form(
        {
          class: 'auth-form',
          'data-testid': 'sign-up-form',
          novalidate: true,
          onSubmit: handleSubmit,
        },
        [
          div({ class: 'auth-form-row' }, [
            label({ for: 'sign-up-email' }, [text(t('auth.sign_up.email_label'))]),
            textInput({
              type: 'email',
              testid: 'sign-up-email',
              attrs: {
                id: 'sign-up-email',
                name: 'email',
                autocomplete: 'username',
                required: true,
                'aria-describedby': 'sign-up-email-error',
              },
            }),
            span(
              {
                id: 'sign-up-email-error',
                'data-error-for': 'sign-up-email',
                class: 'auth-field-error',
                role: 'alert',
              },
              [],
            ),
          ]),
          div({ class: 'auth-form-row' }, [
            label({ for: 'sign-up-password' }, [text(t('auth.sign_up.password_label'))]),
            textInput({
              type: 'password',
              testid: 'sign-up-password',
              attrs: {
                id: 'sign-up-password',
                name: 'password',
                autocomplete: 'new-password',
                minlength: MIN_PASSWORD_LENGTH,
                required: true,
                'aria-describedby': 'sign-up-password-help sign-up-password-error',
              },
            }),
            span({ id: 'sign-up-password-help', class: 'auth-field-help' }, [
              text(t('auth.sign_up.password_help')),
            ]),
            span(
              {
                id: 'sign-up-password-error',
                'data-error-for': 'sign-up-password',
                class: 'auth-field-error',
                role: 'alert',
              },
              [],
            ),
          ]),
          div({ class: 'auth-form-row auth-form-row-inline' }, [
            label({ for: 'sign-up-tos', class: 'auth-checkbox-row' }, [
              input({
                id: 'sign-up-tos',
                name: 'tos',
                type: 'checkbox',
                'data-testid': 'sign-up-tos',
              }),
              span([text(t('auth.sign_up.tos_label'))]),
            ]),
          ]),
          p({ class: 'auth-form-error', 'data-form-error': '', role: 'alert' }, []),
          div({ class: 'auth-form-actions' }, [
            button({
              variant: 'primary',
              type: 'submit',
              testid: 'sign-up-submit',
              children: [text(t('auth.sign_up.submit'))],
            }),
          ]),
        ],
      ),
      div({ class: 'auth-form-links' }, [
        span([
          text(`${t('auth.sign_up.have_account')} `),
          appRouteLink(
            send,
            { kind: 'sign-in' } as AppRoute,
            { 'data-testid': 'sign-up-sign-in-link' },
            [text(t('auth.sign_up.sign_in_link'))],
            (route) => ({ type: 'navigate', route }) as unknown as Msg,
          ),
        ]),
      ]),
    ] }),
  ]);
}
